/**
 * AikoAnchor — declares a stable, namespaced reference to a DOM element so
 * AIKO's reasoning code can point at it without holding a ref.
 *
 *   <AikoAnchor id="wizard.cover-button">
 *     <Button disabled={!hasTitle}>Pick cover</Button>
 *   </AikoAnchor>
 *
 *   // …elsewhere, in the side that decides what AIKO should highlight:
 *   const aiko = useAiko();
 *   aiko.point({
 *     target: "wizard.cover-button",
 *     reason: "Cover sets the tone for the whole studio.",
 *   });
 *
 * Renders nothing of its own — composes with the child via the Radix Slot
 * pattern, stamping `data-aiko-anchor={id}` onto whatever the child renders.
 * The lookup side (`useAikoTarget`, `aiko.point()`) reads that attribute.
 *
 * Why a `data-` attribute and not a registry of refs:
 *
 * - Survives unrelated re-renders. The selector keeps working even when the
 *   child remounts.
 * - Decouples the consumer's component code from the AIKO reasoning code.
 *   Components don't import "the list of hints AIKO might fire at me."
 * - Greppable. Refactoring `wizard.cover-button` is a project-wide
 *   string-replace, not a ref-graph migration.
 *
 * Anchor IDs SHOULD be namespaced by feature: `<feature>.<element>`
 * (e.g. `wizard.cover-button`, `studio.publish-cta`, `aiko-chat.send`).
 * Easy to grep, hard to collide.
 *
 * Use `data-aiko-anchor="..."` directly on the element if you don't need a
 * wrapper. `<AikoAnchor>` is the ergonomic path when the underlying element
 * already accepts an `asChild`-style merge (Button, Link, your own primitive).
 *
 * SSR-safe: the data attribute lives on the child markup, no client-only code.
 */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";

export interface AikoAnchorProps {
  /**
   * Stable, feature-namespaced anchor id (e.g. `"wizard.cover-button"`).
   * Used by `useAikoTarget(id)` and `aiko.point({ target: id })` to find
   * this element in the DOM.
   */
  id: string;
  /** Single React element child; `data-aiko-anchor={id}` is merged onto it. */
  children: React.ReactElement;
}

export const AikoAnchor = React.forwardRef<HTMLElement, AikoAnchorProps>(
  ({ id, children }, ref) => (
    <Slot ref={ref} data-aiko-anchor={id}>
      {children}
    </Slot>
  ),
);
AikoAnchor.displayName = "AikoAnchor";
